<script setup>
/* global axios */
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';

// Меню «наши настройки текущего чата» — заменяет ссылку на Copilot на той же
// кнопке-звёздочке композера. Действия дублируют панель «OVVA — Управление»
// (embed.ejs), но доступны в один клик прямо из диалога, без переключения
// на боковую панель.
//
// Токен запрашивается у Rails ПЕРЕД КАЖДЫМ действием
// (Api::V1::Accounts::OvvaChatTokensController → Ovva::EmbedTokenService).
// Раньше он вшивался в страницу при рендере, и это ломалось двумя способами:
// сбой в момент рендера оставлял пустое значение и меню не работало до
// перезагрузки вкладки, а TTL токена в 15 минут гарантированно истекал у
// оператора, который держит вкладку открытой весь день. Свежий токен на
// каждое действие снимает обе причины, поэтому повтор при 401 не нужен:
// токену на руках всегда несколько миллисекунд.

const props = defineProps({
  conversationId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close']);
const busy = ref(false);
const { accountId } = useAccount();

async function requestCredentials() {
  const { data } = await axios.get(
    `/api/v1/accounts/${accountId.value}/ovva_chat_token`
  );
  return data;
}

async function callBackend(path, method = 'POST') {
  let credentials = null;
  try {
    credentials = await requestCredentials();
  } catch (e) {
    const reason = e?.response?.data?.error;
    useAlert(
      reason
        ? `Панель OVVA недоступна: ${reason}`
        : 'Панель OVVA недоступна — не удалось получить токен'
    );
    return null;
  }

  const base = credentials?.backend_url;
  const token = credentials?.token;
  if (!base || !token) {
    useAlert('Панель OVVA недоступна — бэкенд не выдал токен');
    return null;
  }

  try {
    const res = await fetch(`${base}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401 || res.status === 403) {
      useAlert('Бэкенд OVVA отклонил токен панели — проверьте OVVA_EMBED_TOKEN');
      return null;
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      useAlert(data?.message || 'Не удалось выполнить действие');
      return null;
    }
    return data;
  } catch (e) {
        useAlert(`Бэкенд OVVA не отвечает (${base}) — проверьте, что он запущен`);
    return null;
  }
}

async function runAction(path, successMessage) {
  if (!props.conversationId || busy.value) return;
  busy.value = true;
  const data = await callBackend(`/dialogs/${path}/${props.conversationId}`);
  busy.value = false;
  if (data) {
    useAlert(
      typeof successMessage === 'function' ? successMessage(data) : successMessage
    );
    emit('close');
  }
}

const menuItems = [
  {
    key: 'intercept',
    label: 'Перехватить (забрать у AI)',
    icon: 'i-lucide-hand',
    action: () => runAction('intercept-by-cw', 'Диалог у вас — AI молчит ✋'),
  },
  {
    key: 'release',
    label: 'Вернуть диалог AI',
    icon: 'i-lucide-bot',
    action: () => runAction('release-by-cw', 'Диалог возвращён AI ✅'),
  },
  {
    key: 'hybrid',
    label: 'Гибрид: AI отвечает на безопасное',
    icon: 'i-lucide-shuffle',
    action: () =>
      runAction('hybrid-toggle-by-cw', data =>
        data.aiMode === 'hybrid'
          ? 'Гибрид включён 🔀 AI отвечает на безопасное'
          : 'Гибрид выключен — AI молчит (ручной)'
      ),
  },
];
</script>

<template>
  <DropdownBody class="min-w-64 [&>ul]:gap-2 z-50 [&>ul]:px-3 [&>ul]:py-3">
    <div class="flex flex-col items-start gap-2 w-full">
      <span class="text-xs font-medium text-n-slate-11 px-1">
        OVVA — управление диалогом
      </span>
      <Button
        v-for="item in menuItems"
        :key="item.key"
        :label="item.label"
        :icon="item.icon"
        :disabled="busy || !conversationId"
        slate
        link
        sm
        class="hover:!no-underline text-n-slate-12 font-normal text-xs w-full !justify-start"
        @click="item.action"
      />
    </div>
  </DropdownBody>
</template>
